import { requireSupabase } from './supabase';

function customerAiError(message:string,name:string){
  if(!['generate-mission','ask-learning'].includes(name))return message;
  if(/credits?|billing|quota|insufficient_quota|openai|api[ _-]?key|api account|rate[ _-]?limit|model.*(access|exist|available)|platform\\.openai/i.test(message)){
    return name==='generate-mission'
      ?'We couldn’t prepare your mission right now. Please try again later.'
      :'We couldn’t answer your question right now. Please try again later.';
  }
  return message;
}

async function functionErrorMessage(error:any,name:string){
  try{
    const response=error?.context;
    if(response && typeof response.clone==='function'){
      const clone=response.clone();
      const data=await clone.json().catch(()=>null);
      if(data?.error) return String(data.error);
      if(data?.message) return String(data.message);
      const text=await response.clone().text().catch(()=>'');
      if(text) return text;
      if(response.status) return `${name} failed (${response.status})`;
    }
  }catch{}
  return error?.message || `Could not call ${name}`;
}

async function invoke<T>(name:string, body:Record<string,unknown>):Promise<T>{
  const client=requireSupabase();
  const controller=new AbortController();
  let timer:ReturnType<typeof setTimeout>|undefined;
  const request=(async()=>{
    const {data,error}=await client.functions.invoke(name,{body,signal:controller.signal});
    if(error) throw new Error(customerAiError(await functionErrorMessage(error,name),name));
    if(data?.error) throw new Error(customerAiError(String(data.error),name));
    return data as T;
  })();
  if(!['generate-mission','ask-learning'].includes(name))return request;
  const timeout=new Promise<never>((_,reject)=>{
    timer=setTimeout(()=>{
      reject(new Error(body.mode==='image'?'Your picture is taking longer than expected. Please try again shortly.':name==='generate-mission'
        ?'Your mission is taking longer than expected. Please try again.'
        :'Your answer is taking longer than expected. Please try again.'));
      controller.abort();
    },body.mode==='image'?120000:60000);
  });
  try{return await Promise.race([request,timeout]);}
  finally{if(timer)clearTimeout(timer);}
}

export const api={
  ask:(learnerId:string,question:string,mode:'answer'|'simpler'|'reading'|'challenge'|'short'='answer',context?:string)=>invoke<{answer:string;challenge?:{question:string;hint?:string}}>('ask-learning',{learnerId,question,mode,context}),
  learningSpeech:(learnerId:string,text:string)=>invoke<{audio:string}>('ask-learning',{learnerId,question:text,mode:'speech'}),
  drawLearningImage:(learnerId:string,question:string,context:string)=>invoke<{image:string;alt:string}>('ask-learning',{learnerId,question,context,mode:'image'}),
  aiStatus:(learnerId:string)=>invoke<{connected:boolean;customActive:boolean;platformAvailable:boolean;last4:string|null;connectedAt:string|null;dailyLimit:number;webSearchEnabled:boolean;questionsToday:number;model:string}>('ai-settings',{learnerId,action:'status'}),
  useManagedAi:(learnerId:string)=>invoke<{ok:boolean}>('ai-settings',{learnerId,action:'managed'}),
  saveAiKey:(learnerId:string,key:string)=>invoke<{ok:boolean;last4:string}>('ai-settings',{learnerId,action:'save',key}),
  testAiKey:(learnerId:string)=>invoke<{ok:boolean;last4:string}>('ai-settings',{learnerId,action:'test'}),
  deleteAiKey:(learnerId:string)=>invoke<{ok:boolean}>('ai-settings',{learnerId,action:'delete'}),
  generateMission:(learnerId:string,opts:{subject?:string|null;interest?:string|null;recentQuestions?:string[]})=>invoke<{activities:any[]}>('generate-mission',{learnerId,...opts}),
  youtubeSearch:(learnerId:string,query:string)=>invoke<{items:Array<{videoId:string;title:string;channelTitle:string;thumbnail:string;description:string}>}>('youtube-search',{learnerId,query}),
  youtubePlaylist:(learnerId:string,playlistId:string)=>invoke<{items:Array<{videoId:string;title:string;channelTitle:string;thumbnail:string;description:string}>}>('youtube-playlist',{learnerId,playlistId}),
  deleteAccount:()=>invoke<{ok:boolean}>('delete-account',{confirm:'DELETE'}),
  syncSubscription:()=>invoke<{active:boolean;entitlement:string|null;expiresAt?:string|null}>('sync-subscription',{})
};

